<script>
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { getCardDisplayName } from '../../lib/logic/utils.js';
  import { IMAGE_TIER, IMAGE_TIER_COUNT } from '../../lib/logic/image-load-order.js';
  import { createSlotImageLoad } from '../../lib/stores/image-loader.svelte.js';
  import { longpress } from '../../lib/actions/longpress.js';
  import { tilt } from '../../lib/actions/tilt.js';
  import {
    zoomState,
    zoomOut,
    enableTilt,
    flipDfc,
    finalizeZoomOut,
    getZoomDurationMs,
    EASING_SMOOTH,
    getDfcFlipDurationMs,
  } from '../../lib/stores/zoom.svelte.js';
  import { tradeState, isTradeMode } from '../../lib/stores/trade.svelte.js';
  import { getCardTradeStatus } from '../../lib/logic/trade-logic.js';
  import WantStar from './WantStar.svelte';
  import CardPills from '../shared/CardPills.svelte';
  import { moveToBody } from '../../lib/actions/portal.js';

  let {
    cardInfo = null,
    originalIndex = 0,
    loadTier = IMAGE_TIER_COUNT - 1,
    currencySymbol = '$',
    onSelect,
    onZoom,
    onTradeQuantity,
  } = $props();

  let slotEl = $state(null);

  // Destructure card info
  const status = $derived(cardInfo?.status || 'error');
  const cardData = $derived(cardInfo?.cardData || null);
  const isFoil = $derived(cardInfo?.isFoil || false);
  const finishType = $derived(cardInfo?.finishType || 'nonfoil');
  const foilTypeClass = $derived(cardInfo?.foilTypeClass || null);
  const price = $derived(cardInfo?.price || 0);
  const availableCount = $derived(cardInfo?.availableCount || 0);
  const selectedCount = $derived(cardInfo?.selectedCount || 0);
  const identifier = $derived(cardInfo?.identifier || {});
  const originalLineRef = $derived(cardInfo?.originalLineRef || 'Unknown');

  const isClickable = $derived(status === 'found' && !!cardData);
  const isSelected = $derived(selectedCount > 0);
  // Remember last non-zero count so "0" doesn't flash during CSS fade-out
  let lastSelectedCount = $state(0);
  $effect(() => {
    if (selectedCount > 0) {
      lastSelectedCount = selectedCount;
    }
  });
  const displaySelectedCount = $derived(selectedCount > 0 ? selectedCount : lastSelectedCount);

  // Trade mode state
  const inTradeMode = $derived(isTradeMode());
  const tradeStatus = $derived.by(() => {
    if (!inTradeMode || !cardInfo?.cardData) return null;
    return getCardTradeStatus(cardInfo, tradeState);
  });
  // Remember last non-null trade status so the icon doesn't switch to the
  // default checkmark during the 0.15s CSS fade-out when a card is removed
  let lastTradeStatus = $state(null);
  $effect(() => {
    if (tradeStatus) {
      lastTradeStatus = tradeStatus;
    } else if (!inTradeMode) {
      lastTradeStatus = null;
    }
  });
  // The icon to display: current status, or the remembered status during fade-out
  const displayTradeStatus = $derived(tradeStatus || lastTradeStatus);
  const hasFoilEffect = $derived(
    (isFoil || finishType === 'etched' || finishType === 'glossy') && status === 'found'
  );

  const imageUrl = $derived(isClickable ? getCardImageUrl(cardData) : null);

  const lang = $derived(cardInfo?.lang || cardData?.lang || 'en');
  const isNonEnglish = $derived(lang && lang.toLowerCase() !== 'en');

  const cardName = $derived(isClickable ? getCardDisplayName(cardData) : '');
  const titleText = $derived.by(() => {
    if (status === 'found' && cardData) {
      return `${getCardDisplayName(cardData)} (${cardData.set_name}) - ${currencySymbol}${(parseFloat(price) || 0).toFixed(2)} (Avail: ${availableCount})`;
    }
    if (status === 'not_found') {
      return `Not Found: ${originalLineRef || identifier?.name || '?'}`;
    }
    return `Error: ${originalLineRef || '?'}`;
  });

  const ariaLabel = $derived.by(() => {
    if (!isClickable) return undefined;
    return `${cardName}. ${selectedCount > 0 ? `${selectedCount} selected. ` : ''}Click to ${selectedCount > 0 ? 'deselect' : 'select'}. Right-click or long-press to zoom.`;
  });

  // --- Zoom state for this card ---
  const isZoomed = $derived(zoomState.active && zoomState.cardElement === slotEl);

  // The STAR (the single in-binder want indicator, ADR-0009) lives in WantStar,
  // which owns the want store + auth/OAuth + toggle handler. CardSlot only gates
  // visibility: normal mode only (not trade mode, not zoomed) on real cards.
  const showStar = $derived(isClickable && !inTradeMode && !isZoomed);

  // When zoomed, use large image URL from zoom store
  const zoomedImageUrl = $derived.by(() => {
    if (!isZoomed) return null;
    return zoomState.showingBack ? zoomState.backImageUrl : zoomState.frontImageUrl;
  });

  // The actual displayed image: zoomed large image or normal binder image
  const displayImageUrl = $derived(isZoomed && zoomedImageUrl ? zoomedImageUrl : imageUrl);

  // --- Ordered image loading (IMAGE_LOAD_ORDER.md) ---
  // The <img> gets its src only once this slot's tier is open. The visible
  // spread starts at once; every other face waits for the tiers ahead of it.
  // A zoomed card is on screen whatever page it sits on, so it counts as visible.
  const effectiveTier = $derived(isZoomed ? IMAGE_TIER.VISIBLE : loadTier);
  const fetchPriority = $derived(effectiveTier === IMAGE_TIER.VISIBLE ? 'high' : 'low');
  const slotImage = createSlotImageLoad(
    () => effectiveTier,
    () => imageUrl
  );

  // Zoom animation phases:
  // 1. 'slot' — card is in normal position in binder
  // 2. 'animating-in' — final geometry applied, FLIP transform animating
  //    from the slot to identity (viewport center)
  // 3. 'zoomed' — at viewport center, tilt enabled
  // 4. 'animating-out' — FLIP transform animating from identity back to the
  //    slot rect
  //
  // 'fixed-at-slot' (position: fixed at the slot rect, before any transform)
  // no longer occurs — the FLIP rework applies final geometry immediately
  // and animates a transform on top of it instead of stepping through an
  // intermediate fixed-at-slot frame. sidewaysRotation below still excludes
  // it defensively; it simply never matches.
  let zoomPhase = $state('slot');
  let fixedStyles = $state('');
  let overlaysVisible = $state(true); // false during zoom, true after portal-back + 1 frame

  // While the card is portaled to document.body during zoom, the function
  // that puts it back in its place (a sized placeholder holds the spot).
  /** @type {(() => void) | null} */
  let restoreSlot = null;

  // The currently in-flight zoom-travel WAAPI Animation, if any. Unlike a CSS
  // transition (which a later style write naturally interrupts/redirects from
  // its current computed value), calling `Element.animate()` again does NOT
  // cancel a still-running prior animation on the same property — both keep
  // playing, composited by stacking order, and the earlier one's `.finished`
  // promise still resolves on its own schedule. Without tracking and
  // explicitly cancelling it, pressing Escape mid-zoom-in would let the
  // abandoned zoom-in `.then()` fire ~getZoomDurationMs() later and re-strand
  // the card at viewport center after it had already portaled back to the
  // grid. Tracked
  // here so either effect can cancel the other's animation before starting
  // its own.
  let travelAnim = null;

  function portalToBody() {
    if (!slotEl || slotEl.parentElement === document.body) return;
    // Create a sized placeholder so the grid doesn't reflow
    const computed = getComputedStyle(slotEl);
    const spacer = document.createElement('div');
    spacer.style.width = computed.width;
    spacer.style.height = computed.height;
    spacer.style.aspectRatio = '5 / 7';
    spacer.style.visibility = 'hidden';
    restoreSlot = moveToBody(slotEl, spacer);
  }

  function portalBack() {
    if (!restoreSlot) return;
    restoreSlot();
    restoreSlot = null;
  }

  // DFC flip state: wrapper rotation driven by dfcRotation (starts immediately on flip)
  const dfcRotateY = $derived(isZoomed ? zoomState.dfcRotation : 0);
  // Counter-rotate the image so it reads correctly when the wrapper is flipped
  const dfcImageRotateY = $derived(isZoomed && zoomState.showingBack ? 180 : 0);
  // Sideways rotation — only applied when card is at/heading to center.
  // Excluded during fixed-at-slot (preserves binder orientation) and
  // animating-out (animates back to portrait). Derived from dfcRotation
  // (not showingBack) so DFC flip animates both axes in a single motion.
  const sidewaysRotation = $derived.by(() => {
    if (!isZoomed) return '';
    if (zoomPhase === 'slot' || zoomPhase === 'fixed-at-slot' || zoomPhase === 'animating-out')
      return '';
    const targetIsBack = zoomState.dfcRotation === 180;
    const sideways = targetIsBack ? zoomState.isSidewaysBack : zoomState.isSideways;
    return sideways ? 'rotate(90deg)' : '';
  });

  // dfc-wrapper transition: active during zoom travel and DFC flip
  const dfcWrapperTransition = $derived.by(() => {
    if (!isZoomed) return 'none';
    if (zoomState.isFlipping) return `transform ${getDfcFlipDurationMs()}ms ease-in-out`;
    if (zoomPhase === 'animating-in' || zoomPhase === 'animating-out')
      return `transform ${getZoomDurationMs()}ms ${EASING_SMOOTH}`;
    return 'none';
  });

  // Manage zoom-in animation sequence
  $effect(() => {
    if (isZoomed && zoomPhase === 'slot') {
      const rect = zoomState.slotRect;
      overlaysVisible = false;

      // Move element to body — escapes all stacking contexts
      portalToBody();

      const zoomMs = getZoomDurationMs();
      const maxHeight = window.innerHeight * 0.85;
      const maxWidth = window.innerWidth * 0.9;
      const targetWidth = Math.min(maxWidth, maxHeight * (5 / 7));
      const targetHeight = targetWidth * (7 / 5);
      const centerTop = (window.innerHeight - targetHeight) / 2;
      const centerLeft = (window.innerWidth - targetWidth) / 2;

      // FLIP: apply the FINAL geometry immediately, then animate a transform
      // that makes it *appear* to start at the slot. The old implementation
      // transitioned top/left/width/height — four layout properties, so every
      // frame relaid out and repainted a large image. Transform is
      // compositor-only.
      //
      // transform-origin is top-left so the translate maps the element's
      // corner onto the slot's corner and the scale grows from that same point.
      // Scale is non-uniform so the start frame matches the slot rect exactly
      // even if its aspect ratio differs slightly from the target's — an exact
      // endpoint matters more than a perfectly uniform scale during travel.
      fixedStyles =
        `position: fixed; top: ${centerTop}px; left: ${centerLeft}px; ` +
        `width: ${targetWidth}px; height: ${targetHeight}px; z-index: 1001; ` +
        `margin: 0; aspect-ratio: auto; transform-origin: top left;`;
      zoomPhase = 'animating-in';

      const dx = rect.left - centerLeft;
      const dy = rect.top - centerTop;
      const sx = rect.width / targetWidth;
      const sy = rect.height / targetHeight;

      requestAnimationFrame(() => {
        if (!slotEl) return;
        // Cancel any still-running zoom-out FLIP (shouldn't normally exist
        // here, since a new zoom-in can't start until the previous zoom-out's
        // finalizeZoomOut() runs — kept for symmetry/defensiveness).
        if (travelAnim) travelAnim.cancel();
        const anim = slotEl.animate(
          [
            { transform: `translate3d(${dx}px, ${dy}px, 0) scale(${sx}, ${sy})` },
            { transform: 'translate3d(0, 0, 0) scale(1, 1)' },
          ],
          {
            duration: zoomMs,
            easing: EASING_SMOOTH,
            fill: 'backwards', // hold the start frame until the first tick
            id: 'zoom-travel', // keys this animation in the duration manifest
          }
        );
        travelAnim = anim;

        // A real completion signal, replacing the previous
        // setTimeout(ZOOM_DURATION_MS + 200). No duration/timeout desync is
        // possible here.
        anim.finished
          .then(() => {
            anim.cancel(); // hand `transform` back to inline style for tilt
            if (travelAnim === anim) travelAnim = null;
            if (!slotEl) return;
            zoomPhase = 'zoomed';
            fixedStyles =
              `position: fixed; top: ${centerTop}px; left: ${centerLeft}px; ` +
              `width: ${targetWidth}px; height: ${targetHeight}px; z-index: 1001; ` +
              `margin: 0; aspect-ratio: auto;`; // transform-origin cleared for tilt
            enableTilt();
          })
          .catch(() => {
            // Animation was cancelled (zoom closed early); the close path
            // handles teardown.
            if (travelAnim === anim) travelAnim = null;
          });
      });
    }
  });

  // Manage zoom-out animation (triggered by closing flag)
  $effect(() => {
    if (
      zoomState.closing &&
      zoomState.cardElement === slotEl &&
      zoomPhase !== 'slot' &&
      zoomPhase !== 'animating-out'
    ) {
      const rect = zoomState.slotRect;
      if (!rect || !slotEl) {
        zoomPhase = 'slot';
        fixedStyles = '';
        portalBack();
        finalizeZoomOut();
        return;
      }
      zoomPhase = 'animating-out';

      const zoomMs = getZoomDurationMs();

      // Re-derive the same "base" (untransformed) center geometry the
      // zoom-in effect used — fixedStyles is still set to it (only the
      // transform has been animating), so the FLIP math stays anchored to
      // it here too. window dimensions are assumed stable between zoom-in
      // and zoom-out, same as the rest of this component.
      const maxHeight = window.innerHeight * 0.85;
      const maxWidth = window.innerWidth * 0.9;
      const targetWidth = Math.min(maxWidth, maxHeight * (5 / 7));
      const targetHeight = targetWidth * (7 / 5);
      const centerTop = (window.innerHeight - targetHeight) / 2;
      const centerLeft = (window.innerWidth - targetWidth) / 2;

      // The current rendered box. Normally this equals the base geometry
      // above exactly (transform is identity once zoom-in has settled to
      // 'zoomed'), but if this fires mid-zoom-in (e.g. Escape pressed before
      // the zoom-in FLIP completes), the still-running zoom-in animation
      // means the element is genuinely mid-flight — getBoundingClientRect
      // reflects that live, in-progress transform.
      const box = slotEl.getBoundingClientRect();

      // Cancel any still-running zoom-in FLIP *after* reading box (so box
      // reflects wherever it actually was) but *before* starting our own
      // animation on the same property — Element.animate() calls don't
      // auto-cancel each other like CSS transitions do, so without this the
      // abandoned zoom-in's `.finished` would still resolve later and
      // re-strand the card at viewport center after it's already closed.
      if (travelAnim) travelAnim.cancel();
      slotEl.style.transform = '';
      slotEl.style.transformOrigin = 'top left';

      // Keyframe 0 reproduces the current box relative to the base geometry
      // (identity in the common case; a genuine mid-flight value if
      // interrupted) so zoom-out starts exactly where the card visually is,
      // with no pop. Keyframe 1 reproduces the slot rect, same derivation
      // the zoom-in effect uses.
      const dx0 = box.left - centerLeft;
      const dy0 = box.top - centerTop;
      const sx0 = box.width / targetWidth;
      const sy0 = box.height / targetHeight;
      const dx1 = rect.left - centerLeft;
      const dy1 = rect.top - centerTop;
      const sx1 = rect.width / targetWidth;
      const sy1 = rect.height / targetHeight;

      // Animate DFC flip-back: swap image to front, animate wrapper rotation
      // 180 -> 0. rAF so the dfc-wrapper transition is in the DOM first.
      if (zoomState.isDfc && zoomState.dfcRotation !== 0) {
        requestAnimationFrame(() => {
          zoomState.showingBack = false;
          zoomState.dfcRotation = 0;
        });
      }

      const anim = slotEl.animate(
        [
          { transform: `translate3d(${dx0}px, ${dy0}px, 0) scale(${sx0}, ${sy0})` },
          { transform: `translate3d(${dx1}px, ${dy1}px, 0) scale(${sx1}, ${sy1})` },
        ],
        { duration: zoomMs, easing: EASING_SMOOTH, fill: 'forwards', id: 'zoom-travel' }
      );
      travelAnim = anim;

      anim.finished
        .then(() => {
          anim.cancel();
          if (travelAnim === anim) travelAnim = null;
          portalBack();
          fixedStyles = '';
          if (slotEl) {
            slotEl.style.transform = '';
            slotEl.style.transformOrigin = '';
          }
          zoomPhase = 'slot';
          finalizeZoomOut();
          // Reveal overlays one frame after portal-back so the browser settles
          // the element in the DOM before the CSS transition starts
          requestAnimationFrame(() => {
            overlaysVisible = true;
          });
        })
        .catch(() => {
          /* cancelled */
          if (travelAnim === anim) travelAnim = null;
        });
    }
  });

  function handleClick() {
    if (isZoomed) {
      // DFC flip is handled by dfc-wrapper's onclick (with stopPropagation).
      // If we get here, the click was outside the card image area — close zoom.
      zoomOut();
      return;
    }
    if (!isClickable || !onSelect) return;

    if (inTradeMode) {
      // In trade mode: pass current selectedCount so parent can determine action
      // selectedCount > 0 → parent cycles trade status or removes
      // selectedCount === 0 → parent adds to trade
      onSelect(originalIndex, selectedCount);
    } else {
      const newCount = selectedCount > 0 ? 0 : 1;
      onSelect(originalIndex, newCount);
    }
  }

  function handleDfcClick(e) {
    if (!isZoomed || !zoomState.isDfc || zoomPhase !== 'zoomed') return;
    e.stopPropagation();
    flipDfc();
  }

  function handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }

  function handleContextMenu(e) {
    if (!isClickable) return;
    e.preventDefault();
    onZoom?.(originalIndex, cardInfo, slotEl);
  }

  function handleDecrease(e) {
    e.stopPropagation();
    if (inTradeMode && onTradeQuantity) {
      if (selectedCount <= 1) {
        // Going to 0 = remove from trade (cycle to remove)
        onSelect?.(originalIndex, selectedCount);
      } else {
        onTradeQuantity(originalIndex, selectedCount - 1);
      }
    } else if (selectedCount > 0) {
      onSelect?.(originalIndex, selectedCount - 1);
    }
  }

  function handleIncrease(e) {
    e.stopPropagation();
    if (inTradeMode && onTradeQuantity) {
      if (selectedCount < availableCount) {
        onTradeQuantity(originalIndex, selectedCount + 1);
      }
    } else if (selectedCount < availableCount) {
      onSelect?.(originalIndex, selectedCount + 1);
    }
  }

  function handleRemoveAll(e) {
    e.stopPropagation();
    if (inTradeMode) {
      // In trade mode: cycle status (same as clicking the card body)
      onSelect?.(originalIndex, selectedCount);
    } else {
      onSelect?.(originalIndex, 0);
    }
  }

  function handleImageError(e) {
    slotImage.release();
    const altText = e.target.alt || 'Load Error';
    e.target.src = `https://placehold.co/245x342/f8d7da/721c24?text=${encodeURIComponent(altText.substring(0, 10))}+Err`;
    e.target.onerror = null;
  }
</script>

{#if status === 'placeholder'}
  <!-- Collection placeholder (loading spinner) -->
  <div class="card-slot placeholder-loading" data-original-index={originalIndex}>
    <div class="card-content">
      <div class="card-placeholder-spinner"></div>
    </div>
  </div>
{:else}
  <!-- Real card slot -->
  <!-- role="button" and tabindex="0" are both gated on the same isClickable predicate, so a
       focusable slot is always a button; the compiler can't see that coupling statically. -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div
    class="card-slot"
    class:selected={isSelected && !isZoomed}
    class:is-foil={hasFoilEffect}
    class:foil-type-old-border={foilTypeClass === 'foil-type-old-border'}
    class:foil-type-old-border-green={foilTypeClass === 'foil-type-old-border-green'}
    class:foil-type-old-border-black={foilTypeClass === 'foil-type-old-border-black'}
    class:is-zoomed={isZoomed || zoomPhase !== 'slot'}
    data-clickable={isClickable ? 'true' : 'false'}
    data-original-index={originalIndex}
    style:cursor={isClickable ? 'pointer' : 'default'}
    style={fixedStyles || undefined}
    role={isClickable ? 'button' : undefined}
    tabindex={isClickable ? 0 : undefined}
    aria-label={ariaLabel}
    onclick={handleClick}
    onkeydown={isClickable ? handleKeydown : undefined}
    oncontextmenu={handleContextMenu}
    use:longpress={{
      onLongPress: isClickable ? () => onZoom?.(originalIndex, cardInfo, slotEl) : undefined,
    }}
    use:tilt={{ enabled: zoomState.tiltEnabled && isZoomed && zoomPhase === 'zoomed' }}
    bind:this={slotEl}
  >
    <div
      class="card-content"
      class:zoom-hidden={zoomState.active && !isZoomed && zoomState.cardElement === slotEl}
    >
      {#if status === 'found' && cardData}
        <!-- DFC wrapper for flip rotation -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="dfc-wrapper"
          class:dfc-clickable={isZoomed && zoomState.isDfc && zoomPhase === 'zoomed'}
          style:transform="{sidewaysRotation} rotateY({dfcRotateY}deg)"
          style:transition={dfcWrapperTransition}
          onclick={handleDfcClick}
        >
          {#if displayImageUrl && slotImage.started}
            <img
              src={displayImageUrl}
              alt={cardData.name}
              title={isZoomed ? undefined : titleText}
              fetchpriority={fetchPriority}
              width="488"
              height="680"
              style:transform={dfcImageRotateY ? `rotateY(${dfcImageRotateY}deg)` : undefined}
              onload={slotImage.release}
              onerror={handleImageError}
            />
          {:else if displayImageUrl}
            <!-- Queued behind an earlier load tier: a src-less img keeps the card
                 box (and the perf fixture's "every img decoded" wait honest) until
                 its turn. src stays unconditional above: a conditional src binding
                 changed how the zoom-in overlay fade behaved (animation gate). -->
            <img class="queued" alt="" title={titleText} width="488" height="680" />
          {:else}
            <div class="card-placeholder" title={titleText}>
              {cardData.name} (No Img)
            </div>
          {/if}
        </div>

        {#if hasFoilEffect}
          <!-- Foil sheen. A static clip wrapper carries the mask and rounding so
               neither is recomputed per frame; only the oversized child moves,
               and it moves via transform, which the compositor handles without
               repainting the card. -->
          <div class="foil-sheen-clip" aria-hidden="true">
            <div class="foil-sheen"></div>
          </div>
        {/if}

        <!-- Binder overlays — fade in/out on zoom transitions (300ms matches original) -->
        <div class="binder-overlays" class:visible={overlaysVisible}>
          <CardPills {price} foil={isFoil} quantity={availableCount} {currencySymbol} />

          {#if isNonEnglish}
            <div class="language-indicator" class:lang-long={lang.length > 2}>
              {lang.toUpperCase()}
            </div>
          {/if}

          <!-- Want List STAR — extracted into WantStar (ADR-0009; review §5),
               which owns the want store, auth/OAuth, and the toggle handler.
               CardSlot only decides when to show it. -->
          {#if showStar}
            <WantStar card={cardData} {cardName} />
          {/if}

          <!-- Selection controls overlay (both normal and trade modes) -->
          <div class="selection-controls">
            {#if inTradeMode && displayTradeStatus}
              <!-- Trade mode: status-specific icon (uses displayTradeStatus to
                   preserve the icon during CSS fade-out when card is removed) -->
              <div
                class="selected-checkmark status-{displayTradeStatus}"
                role="button"
                tabindex="0"
                title="Cycle trade status for {cardName}"
                aria-label="{tradeStatus} status for {cardName}. Click to cycle."
                onclick={handleRemoveAll}
                onkeydown={(e) => {
                  if (e.key === 'Enter') handleRemoveAll(e);
                }}
              ></div>
            {:else}
              <!-- Normal mode: default checkmark -->
              <div
                class="selected-checkmark"
                role="button"
                tabindex="0"
                title="Remove all {cardName}"
                aria-label="Remove all {selectedCount} selected {cardName}"
                onclick={handleRemoveAll}
                onkeydown={(e) => {
                  if (e.key === 'Enter') handleRemoveAll(e);
                }}
              ></div>
            {/if}

            {#if availableCount > 1}
              <div class="quantity-adjust-buttons">
                <button
                  class="adjust-btn minus-btn"
                  disabled={selectedCount <= 0}
                  aria-label="Decrease quantity of {cardName}. Currently {selectedCount} of {availableCount} selected."
                  onclick={handleDecrease}>-</button
                >
                <span class="selected-quantity-display" aria-live="polite" aria-atomic="true">
                  {displaySelectedCount}
                </span>
                <button
                  class="adjust-btn plus-btn"
                  disabled={selectedCount >= availableCount}
                  aria-label="Increase quantity of {cardName}. Currently {selectedCount} of {availableCount} selected."
                  onclick={handleIncrease}>+</button
                >
              </div>
            {/if}
          </div>
        </div>
      {:else if status === 'not_found'}
        <div class="card-placeholder is-error" title={titleText}>
          Not Found: {identifier?.name || originalLineRef || '?'}
        </div>
      {:else if status === 'error'}
        <div class="card-placeholder is-error" title={titleText}>
          Error: {status}
        </div>
      {:else}
        <!-- Empty slot -->
        <div class="card-placeholder"></div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .card-slot {
    border-radius: var(--card-corner-radius, 4%);
    overflow: visible;
    aspect-ratio: 5 / 7;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
    perspective: 1000px;
    z-index: 2;
    background-color: transparent;
    box-shadow: none;
    contain: style;
    user-select: none;
    -webkit-user-select: none;
    height: auto;
    min-width: 0;
  }

  .card-slot[data-clickable='false'] {
    cursor: default;
  }

  /* When zoomed, override binder positioning */
  .card-slot.is-zoomed {
    overflow: visible;
    contain: none;
    perspective: none;
    border-radius: var(--card-corner-radius, 4%);
  }

  .card-content {
    width: 100%;
    height: 100%;
    position: relative;
    transition: opacity var(--dur-drawer);
    border-radius: var(--card-corner-radius, 4%);
    background-color: transparent;
    box-shadow: none;
    overflow: visible;
  }

  /* DFC wrapper for flip rotation */
  .dfc-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: var(--card-corner-radius, 4%);
    overflow: hidden;
  }

  .dfc-wrapper.dfc-clickable {
    cursor: pointer;
  }

  .card-slot img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: var(--card-corner-radius, 4%);
    pointer-events: none;
  }

  /* Queued behind an earlier load tier: a blank card back until its turn */
  .card-slot img.queued {
    background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  }

  /* Foil sheen — compositor-only.
     The previous implementation animated `background-position` on a ::after,
     which is a paint property: every frame repainted every foil card, on a
     buffer of up to 144 slots, forever. This version moves an oversized child
     with `transform` instead.

     Travel derivation, so the motion is identical to the old version:
       Old: background-size 300% (image width = 3C, C = card width),
            background-position 200% -> -100%.
       For background-position X%, offset = X * (containerW - imageW)
                                         = X * (C - 3C) = X * (-2C).
       X = 200%  ->  offset = -4C
       X = -100% ->  offset = +2C
       As a translate on a 3C-wide element (translate % is of its own width):
       -4C / 3C = -133.333%   and   +2C / 3C = +66.667%. */
  .foil-sheen-clip {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: var(--card-corner-radius, 4%);
    opacity: 0.6;
    mix-blend-mode: screen;
    z-index: var(--z-card-foil, 5);
    pointer-events: none;
  }

  .foil-sheen {
    position: absolute;
    top: 0;
    left: 0;
    width: 300%;
    height: 100%;
    background: linear-gradient(
      100deg,
      transparent 30%,
      rgba(255, 255, 255, 0.15) 45%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0.15) 55%,
      transparent 70%
    );
    animation: foil-sheen var(--dur-foil-sheen) linear infinite;
    will-change: transform;
  }

  @keyframes foil-sheen {
    from {
      transform: translate3d(-133.333%, 0, 0);
    }
    to {
      transform: translate3d(66.667%, 0, 0);
    }
  }

  /* Retro frame foil masks — on the static wrapper, so the mask is rasterised
     once instead of per frame. */
  .card-slot.is-foil[class*='foil-type-'] .foil-sheen-clip {
    -webkit-mask-image: var(--foil-mask-url);
    mask-image: var(--foil-mask-url);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
  }

  /* Reduced motion: hold the sheen still rather than speeding it up — a
     0.01ms infinite loop would spin. */
  @media (prefers-reduced-motion: reduce) {
    .foil-sheen {
      animation: none;
      transform: translate3d(-33%, 0, 0);
    }
  }

  .card-slot.foil-type-old-border {
    --foil-mask-url: url('/masks/old-border-mask.png');
  }
  .card-slot.foil-type-old-border-green {
    --foil-mask-url: url('/masks/old-border-mask-green.png');
  }
  .card-slot.foil-type-old-border-black {
    --foil-mask-url: url('/masks/old-border-mask-black.png');
  }

  /* Binder overlays container — fades in/out on zoom (300ms matches original CARD_CONTENT_FADE_MS) */
  .binder-overlays {
    position: absolute;
    inset: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--dur-drawer) ease;
    z-index: var(--z-card-overlay, 8);
  }

  .binder-overlays.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .language-indicator {
    position: absolute;
    bottom: 5px;
    left: 5px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #1e2224;
    border: 2px solid #fff;
    color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    text-transform: uppercase;
    z-index: var(--z-card-language, 6);
    pointer-events: none;
  }

  .language-indicator.lang-long {
    font-size: 7px;
  }

  /* Selection controls overlay */
  .selection-controls {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: var(--z-card-selection, 7);
    border-radius: var(--card-corner-radius, 4%);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity var(--dur-fast) ease-in-out,
      visibility 0s linear var(--dur-fast);
  }

  .card-slot.selected .selection-controls {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition:
      opacity var(--dur-fast) ease-in-out,
      visibility 0s linear 0s;
  }

  .selected-checkmark {
    width: 40px;
    height: 40px;
    cursor: pointer;
    margin-bottom: 6px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'%3E%3C/path%3E%3Cpolyline points='22 4 12 14.01 9 11.01'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 70%;
    transition: background-color var(--dur-base);
  }

  /* Trade status icon variants (same size/position, different SVGs) */
  .selected-checkmark.status-trade {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'%3E%3C/path%3E%3Cpolyline points='22 4 12 14.01 9 11.01'%3E%3C/polyline%3E%3C/svg%3E");
  }

  .selected-checkmark.status-considering {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'%3E%3C/path%3E%3Ccircle cx='12' cy='12' r='3'%3E%3C/circle%3E%3C/svg%3E");
  }

  .selected-checkmark.status-suggested {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'%3E%3C/circle%3E%3Cpath d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'%3E%3C/path%3E%3Cline x1='12' y1='17' x2='12.01' y2='17'%3E%3C/line%3E%3C/svg%3E");
  }

  .quantity-adjust-buttons {
    display: flex;
    align-items: center;
  }

  .adjust-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: white;
    font-weight: bold;
    font-size: 18px;
    line-height: 26px;
    text-align: center;
    cursor: pointer;
    margin: 0 4px;
    transition:
      background-color var(--dur-base),
      transform var(--dur-instant);
    border: 1px solid rgba(255, 255, 255, 0.3);
    user-select: none;
    background-color: rgba(55, 65, 81, 0.7);
  }

  .adjust-btn:hover {
    background-color: rgba(31, 41, 55, 0.8);
  }

  .adjust-btn:active {
    transform: scale(0.95);
    background-color: rgba(17, 24, 39, 0.85);
  }

  .adjust-btn:disabled {
    background-color: rgba(107, 114, 128, 0.5);
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    border-color: rgba(255, 255, 255, 0.1);
  }

  .selected-quantity-display {
    color: white;
    font-weight: bold;
    font-size: 14px;
    min-width: 20px;
    text-align: center;
    pointer-events: none;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Card placeholder (not-found / error / empty) */
  .card-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0.5rem;
    font-size: 0.75rem;
    text-align: center;
    word-break: break-word;
    font-weight: 500;
    color: #6b7280;
    border-radius: var(--card-corner-radius, 4%);
  }

  .card-placeholder.is-error {
    color: #721c24;
  }

  /* Collection loading placeholder */
  .placeholder-loading {
    background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
    border-radius: var(--card-corner-radius, 4%);
    overflow: hidden;
  }

  .placeholder-loading .card-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: transparent;
  }

  .card-placeholder-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-top-color: rgba(255, 255, 255, 0.4);
    border-radius: 50%;
    animation: placeholder-spin 1s linear infinite;
  }

  @keyframes placeholder-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .placeholder-loading::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.05) 50%,
      transparent 100%
    );
    animation: placeholder-shimmer 1.5s ease-in-out infinite;
    border-radius: var(--card-corner-radius, 4%);
  }

  @keyframes placeholder-shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  /* Stitching pseudo-elements are on the parent CardGrid via :global */
</style>
